import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  TextField,
  Alert,
} from '@mui/material';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

export default function Login() {
  const navigate = useNavigate();
  const { setTokens, setCurrentAccountId, user } = useAuth();
  const { access, isLoading } = useAuth();

  /* local form state */
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);


  if (!isLoading && access) {
    // Redirect based on user role
    const redirectPath = user?.role === 'CASE_REVIEWER' ? '/reviews'
      : user?.role === 'SCHEME_VIEWER' ? '/schemes'
      : '/home';
    return <Navigate to={redirectPath} />;
  }

  /* ── username / password flow ─────────────────── */
  const passwordLogin = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login/', {
        username: u,
        password: p,
      });
      await onLoginSuccess(data);
      // setTokens(data.access, data.refresh);
      // navigate('/cases');
    } catch {
      setErr('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const onLoginSuccess = async (data: { access: string; refresh: string }) => {
    setTokens(data.access, data.refresh); // stores in localStorage + state

    // fetch /me and grab default_account_id + role
    const me = await api.get('/users/me/');
    const defaultAcct = me.data.default_account_id;
    const userRole = me.data.role;

    if (defaultAcct) {
      localStorage.setItem('current_account_id', defaultAcct);
      setCurrentAccountId(defaultAcct);
    }

    // Redirect based on user role
    const redirectPath = userRole === 'CASE_REVIEWER' ? '/reviews'
      : userRole === 'SCHEME_VIEWER' ? '/schemes'
      : '/home';
    navigate(redirectPath);
  };

  /* ── UI ───────────────────────────────────────── */
  // Debug logging
  console.log('🔍 Google Client ID:', process.env.REACT_APP_GOOGLE_CLIENT_ID);
  console.log('🔍 All REACT_APP env vars:', Object.keys(process.env).filter(k => k.startsWith('REACT_APP')));

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID!}>
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card sx={{ maxWidth: 420, width: '100%', boxShadow: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Sign in to EliotNest
            </Typography>

            {err && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
                {err}
              </Alert>
            )}

            {/* Google OAuth button */}
            <GoogleLogin
              size="large"
              text="signin_with"
              shape="pill"
              useOneTap={false}
              ux_mode="popup"
              onSuccess={async (resp) => {
                console.log('Google login success:', resp);
                if (!resp.credential) return;
                try {
                  const { data } = await api.post('/auth/google/', {
                    token: resp.credential,
                  });
                  await onLoginSuccess(data);
                  // setTokens(data.access, data.refresh);  // ← store both
                  // navigate('/cases');
                } catch (err) {
                  console.error('Backend auth error:', err);
                  setErr('Google sign-in failed');
                }
              }}
              onError={() => {
                console.error('Google login error');
                setErr('Google sign-in failed');
              }}
            />

            <Divider sx={{ my: 3 }}>or</Divider>

            {/* username / password form */}
            <TextField
              label="Username"
              fullWidth
              size="small"
              margin="dense"
              value={u}
              onChange={(e) => setU(e.target.value)}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              size="small"
              margin="dense"
              value={p}
              onChange={(e) => setP(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && passwordLogin()}
            />

            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 2 }}
              disabled={loading}
              onClick={passwordLogin}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </CardContent>
        </Card>
      </Box>
    </GoogleOAuthProvider>
  );
}
